import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, CheckCircle2 } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/customerApi";
import { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function AccountAuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { reload } = useCustomerAuth();
  const processed = useRef(false);

  const [phase, setPhase] = useState("processing"); // processing | profile | error
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setPhase("error");
      return;
    }
    processed.current = true;
    const sessionId = decodeURIComponent(match[1]);
    window.history.replaceState(null, "", window.location.pathname);

    (async () => {
      try {
        const { data } = await customerApi.post("/account/google/session", { session_id: sessionId });
        if (data.status === "ok") {
          await reload();
          toast.success("Signed in");
          nav("/account");
        } else if (data.status === "pending") {
          toast.info("Your trade account is awaiting approval. We'll be in touch shortly.");
          nav("/account/login");
        } else if (data.status === "profile_incomplete") {
          setName(data.name || "");
          setPhase("profile");
        } else {
          setPhase("error");
        }
      } catch (err) {
        toast.error(formatApiError(err));
        setPhase("error");
      }
    })();
  }, [location.hash, nav, reload]);

  const submitProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await customerApi.post("/account/google/complete-profile", { company, phone });
      if (data.status === 'ok') {
        await reload();
        nav('/account');
      } else {
        toast.success("Thanks — your trade account is awaiting approval.");
        nav("/account/login");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Seo title="Signing in — EI Battery Solutions" description="Completing your trade account sign-in." />
      <section className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="account-google-callback">
        {phase === "processing" && (
          <div className="text-center py-10" data-testid="google-callback-processing">
            <Loader2 className="w-10 h-10 text-ei-golddark mx-auto animate-spin" strokeWidth={1.5} />
            <p className="mt-4 text-sm text-ei-graphite/75">Signing you in…</p>
          </div>
        )}

        {phase === "profile" && (
          <div data-testid="google-profile-form-wrap">
            <div className="flex items-center gap-2 text-ei-golddark">
              <Building2 className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-wide">Trade accounts</span>
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-ei-navy">Complete your trade profile</h1>
            <p className="mt-2 text-sm text-ei-graphite/75">
              {name ? `Welcome, ${name}. ` : ""}Add your business details so our team can review and approve your
              account.
            </p>
            <form onSubmit={submitProfile} className="mt-8 space-y-5" data-testid="google-profile-form">
              <label className="block">
                <span className="text-xs font-semibold text-ei-navy">Business name</span>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  data-testid="google-profile-company"
                  className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-ei-navy">Mobile</span>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  data-testid="google-profile-phone"
                  className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
                />
              </label>
              <button
                type="submit"
                disabled={saving}
                data-testid="google-profile-submit"
                className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Submit for approval
              </button>
              <p className="text-xs text-ei-graphite/55">Trade customers only. Access is granted after our team reviews your details.</p>
            </form>
          </div>
        )}

        {phase === "error" && (
          <div className="text-center py-10" data-testid="google-callback-error">
            <h1 className="text-xl font-bold text-ei-navy">Sign-in couldn't be completed</h1>
            <p className="mt-2 text-sm text-ei-graphite/75">Please try signing in again.</p>
            <button
              onClick={() => nav("/account/login")}
              data-testid="google-callback-back"
              className="mt-6 font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3 rounded-sm hover:bg-ei-ink transition-colors"
            >
              Back to login
            </button>
          </div>
        )}
      </section>
    </>
  );
}
